import {
  FontWeight,
  Heading,
  LiliLinkButton,
  LiliLinkType,
  LiliOtp,
  Spinner,
  SpinnerColor,
  Text,
  LiliCheckboxNew,
} from '@lili/lili-frontend-common';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useBlocker } from '@tanstack/react-router';
import { ControllerWrapper } from '@Hooks/index';
import { MFA_TYPES, OnboardingFormFields } from '@Types/index';
import { useBoundStore } from '@Store/index.ts';
import { convertToTimeFormat } from '@Services/utils/utils';
import { SuccessLayoutWrapper } from '@Components/successLayoutWrapper';
import { useAutoLoginPageLogic } from './hooks/useAutoLoginLogic';
import type React from 'react';
import type { WebAppLoginResponse } from './api/autoLogin.api';
export const AutoLoginPage: React.FC<{
  webAppResponseData: WebAppLoginResponse | undefined | null;
}> = ({ webAppResponseData }) => {
  useBlocker({
    shouldBlockFn: () => true,
  });

  const formattedMfaDestination = webAppResponseData?.mfaDestination?.replace(/\*/g, '•');
  const { t } = useTranslation();
  const {
    formMethods,
    onChange,
    isLoadingVerifyMfa,
    otpLength,
    timeLeft,
    handleSendCode,
    isLoadingResendLoginMfa,
  } = useAutoLoginPageLogic(
    webAppResponseData?.mfaType ?? MFA_TYPES.SMS,
    webAppResponseData?.loginResponseCustomData ?? {}
  );
  const {
    customer: {
      customerData: { firstName },
    },
  } = useBoundStore((state) => state);

  return (
    <>
      <FormProvider {...formMethods}>
        <SuccessLayoutWrapper>
          <div className='flex mt-[40px] md:mt-[100px] flex-col items-center justify-center'>
            <Heading.H5>{t('autoLogin.congratulations', { firstName })}</Heading.H5>
            <Heading.H3 fontWeight={FontWeight.SEMIBOLD}>{t('autoLogin.accountReady')}</Heading.H3>
          </div>
          <div className='flex flex-col items-center justify-center pt-[32px] md:pt-[52px] pb-[16px] md:pb-[22px] px-[24px]'>
            <Text.H9 className='text-surface-primary-bw-07 text-center'>
              {t('autoLogin.verificationCodeSent')}
            </Text.H9>
            <Text.H9 className='text-surface-primary-bw-07 text-center'>
              {t('autoLogin.sentTo', { mfaDestination: formattedMfaDestination })}
            </Text.H9>
          </div>
          <ControllerWrapper name={OnboardingFormFields.MFA_OTP}>
            {({ error, onChange: onChangeController }) => (
              <>
                <LiliOtp
                  id={OnboardingFormFields.MFA_OTP}
                  maxLength={otpLength}
                  onChange={(value) => {
                    onChangeController(value);
                    onChange();
                  }}
                  disabled={isLoadingVerifyMfa}
                  errorMessage={error?.message}
                  autoFocus
                />
                {isLoadingVerifyMfa ||
                  (isLoadingResendLoginMfa && (
                    <div className='self-center -mt-[32px]'>
                      <Spinner height='24px' width='24px' spinnerColor={SpinnerColor.Blue} />
                    </div>
                  ))}
              </>
            )}
          </ControllerWrapper>
          <div className='flex items-center justify-center mt-[24px] md:mt-[40px] pb-[16px] md:pb-[24px] px-[24px]'>
            <ControllerWrapper name={OnboardingFormFields.REMEMBER_ME}>
              {({ value, onChange: onCheckboxChange }) => (
                <LiliCheckboxNew
                  id={OnboardingFormFields.REMEMBER_ME}
                  checked={Boolean(value)}
                  onCheckedChange={(isChecked) => onCheckboxChange(isChecked)}
                  label={
                    <Text.H9 className='text-text-main-secondary'>
                      {t('autoLogin.rememberDevice')}
                    </Text.H9>
                  }
                  labelAlign='start'
                />
              )}
            </ControllerWrapper>
          </div>
          <div className='flex h-full text-center justify-center flex-col gap-[16px] md:gap-[24px] mb-[24px] md:mb-[40px] px-[24px]'>
            <div className='flex text-center justify-center flex-col gap-[8px]'>
              <Heading.H9 fontWeight={FontWeight.SEMIBOLD}>
                {t('autoLogin.noCodeReceived')}
              </Heading.H9>

              {!timeLeft && !isLoadingVerifyMfa && (
                <LiliLinkButton
                  type={LiliLinkType.DEFAULT}
                  title={t('autoLogin.resendCode')}
                  id='validate-phone-resend-code'
                  onClick={handleSendCode}
                  className='self-center'
                />
              )}

              {timeLeft && !isLoadingVerifyMfa && !isLoadingResendLoginMfa && (
                <div className='flex items-center gap-[4px] self-center'>
                  <Heading.H9>
                    {t('autoLogin.resendCodeTimer', {
                      timeLeft: convertToTimeFormat(timeLeft),
                    })}
                  </Heading.H9>
                </div>
              )}
            </div>
            <Text.H12 className='text-text-main-tertiary md:px-[10px] px-[4px] md:whitespace-pre-line mb-1 '>
              {t('autoLogin.disclaimer')}
            </Text.H12>
          </div>
        </SuccessLayoutWrapper>
      </FormProvider>
    </>
  );
};

export default AutoLoginPage;
